import React, { useEffect } from 'react';
import { Redirect } from 'react-router';
import { track } from '@amplitude/analytics-browser';
import { Box } from '@nimbus-ds/components';
import { BaseAppLayout } from 'App/components/AppLayout';
import { ROUTES } from 'App/Routes/constants';
import NotificationProvider from 'commons/components/NotificationProvider/NotificationProvider';
import {
  useGetConcessionPaymentProviders,
  useHasTransactions,
  useIdentityValidation,
  useStoreInfo,
} from 'commons/hooks';
import EmptyStateArgentinaSkeleton from './EmptyStateArgentinaSkeleton';
import EmptyStateFirstSection from './EmptyStateFirstSection';
import EmptyStateNotifications from './EmptyStateNotifications';
import EmptyStateSecondSection from './EmptyStateSecondSection';
import EmptyStateThirdSection from './EmptyStateThirdSection';
import InvalidIndustry from './InvalidIndustry';

function EmptyStateArgentina(): JSX.Element {
  const { storeInfo } = useStoreInfo();
  const {
    isLoading: idValidationLoading,
    statusDeniedAndInvalidIndustry,
    data: idValidationData,
  } = useIdentityValidation();
  const { hasTransactions, isLoading: transactionsLoading } =
    useHasTransactions();
  const {
    data: concessionPaymentProvidersData,
    isLoading: isLoadingConcessionPaymentProviders,
  } = useGetConcessionPaymentProviders();

  const isPagoNubeEnabled = Boolean(concessionPaymentProvidersData?.enabled);

  useEffect(() => {
    if (!transactionsLoading && !hasTransactions) {
      track('pagonube_landing_empty_state_view', {
        cohortType: storeInfo.active ? 'old_merchant' : 'new_merchant',
      });
    }
  }, [transactionsLoading, hasTransactions, storeInfo.active]);

  if (
    transactionsLoading ||
    idValidationLoading ||
    isLoadingConcessionPaymentProviders
  )
    return <EmptyStateArgentinaSkeleton />;
  if (hasTransactions) return <Redirect to={ROUTES.HOME} />;

  if (statusDeniedAndInvalidIndustry) {
    return <InvalidIndustry />;
  }

  const validationStatus = idValidationData?.status;
  const validationUpdatedAt = idValidationData?.updatedAt;
  const kycDocumentsErrors = idValidationData?.kycDocumentErrors;
  const trialPeriod = idValidationData?.trialPeriod;
  const blockedTrialPeriod = idValidationData?.blockedTrialPeriod;
  const validationStatusDetail = idValidationData?.statusDetail;

  return (
    <NotificationProvider>
      <BaseAppLayout>
        <Box
          display="flex"
          flexDirection="column"
          gap="16"
          padding="4"
          data-testid="empty-state-argentina"
        >
          <EmptyStateNotifications />
          <EmptyStateFirstSection
            isNewCohort={!storeInfo.active}
            isPagoNubeEnabled={isPagoNubeEnabled}
            validationStatus={validationStatus}
            validationUpdatedAt={validationUpdatedAt}
            kycDocumentsErrors={kycDocumentsErrors}
            trialPeriod={trialPeriod}
            blockedTrialPeriod={blockedTrialPeriod}
            validationStatusDetail={validationStatusDetail}
          />
          <EmptyStateSecondSection />
          <EmptyStateThirdSection />
        </Box>
      </BaseAppLayout>
    </NotificationProvider>
  );
}

export default EmptyStateArgentina;
